<template>
  <PageTemplate>
    <div class="row">
      <div class="col-lg-4">
        <h1>
          Job Number: {{ jobNo }}
          <v-badge
            :text="jobStatusBadgeText"
            :type="jobStatusBadgeValue"
            style="font-size: 1.25rem;"
          />
        </h1>
      </div>
      <div class="col-lg-8">
        <v-step-indicator :statuses="statuses" />
      </div>
    </div>

    <div class="row">
      <div class="col-lg-4 d-flex">
        <v-card title="Job Details">
          <v-definition-list :data="jobDetails" />
        </v-card>
      </div>
      <div class="col-lg-4 d-flex">
        <v-card title="Site Details">
          <v-definition-list :data="siteDetails" />
        </v-card>
      </div>
      <div class="col-lg-4 d-flex">
        <v-card title="Lead Assessor">
          <v-definition-list :data="LeadAssessor" />
        </v-card>
      </div>
    </div>
    <v-tabs>
      <v-tab
        name="Received Job Documents"
        :selected="true"
      >
        <v-table
          ref="receivedDocumentTable"
          :table="received"
          :checkboxes-enabled="true"
          :sort-options="{
            enabled: true,
            initialSortBy: {field: 'dateUploaded', type: 'asc'}
          }"
          @showDoc="previewDoc"
          @downloadDoc="downloadDoc"
          @onChecked="showReview"
        >
          <template #optional>
            <v-button
              color="primary"
              @click="downloadSelectedReceived"
            >
              Download Selected
            </v-button>
          </template>
        </v-table>
      </v-tab>
      <v-tab name="Submitted Job Documents">
        <v-table
          ref="submittedDocumentTable"
          :table="submitted"
          :checkboxes-enabled="true"
          :sort-options="{
            enabled: true,
            initialSortBy: {field: 'dateUploaded', type: 'asc'}
          }"
          @showDoc="previewDoc"
          @downloadDoc="downloadDoc"
        >
          <template #optional>
            <v-button
              color="primary"
              @click="downloadSelectedSubmitted"
            >
              Download Selected
            </v-button>
          </template>
        </v-table>
        <v-button
          size="lg"
          @click="openModal"
        >
          Upload Document
        </v-button>
      </v-tab>
    </v-tabs>
    <v-modal
      v-if="showModal"
      @close="closeModal"
    >
      <template #header>
        File Upload
      </template>
      <template v-if="file">
        <div
          v-if="fileConfirmed"
          class="row align-items-center"
        >
          <div class="col-lg-6">
            <v-fieldset
              v-model="uploadDescription"
              type="textarea"
              text="Details/Report Reference*"
              maxlength="255"
              @input="inputChange"
            />
            <div
              class="max-char-text"
            >
              (*mandatory field, 255 character maximum.)
            </div>
          </div>
          <div class="col-lg-6">
            <v-fileupload
              v-if="file"
              :file-name="file.name"
              :file-size="file.size | formatFileSize"
              @clear="fileRemoved"
            />
          </div>
          <v-spinner
            v-if="isUploading"
            :is-full="false"
          />
        </div>
        <div
          v-else
          class="text--center"
        >
          <p class="text--lg">
            Are you sure this is the file you want to upload to Job {{ jobNo }}?
          </p>
          <p class="text--lg">
            <strong>{{ file.name }}</strong> {{ file.size | formatFileSize }}
          </p>
          <v-button-group is-inline>
            <v-button
              v-if="file"
              has-min-width
              @click="fileConfirmed = true"
            >
              Yes
            </v-button>
            <v-button
              color="secondary"
              has-min-width
              @click="fileRemoved"
            >
              No
            </v-button>
          </v-button-group>
        </div>
      </template>
      <v-dropzone
        v-else
        additional-msg="(Avoid using special characters (!@#$%^&*_+/) in filenames,  these will be replaced and converted into acceptable file name format. Multiple files are best uploaded as a zipped file folder.)"
        @file-added="fileAdded"
        @file-error="uploadError"
      />
      <template #footer>
        <v-button-group is-right>
          <v-button
            v-if="file && fileConfirmed"
            size="lg"
            has-min-width
            @click="uploadDocument"
          >
            Finish
          </v-button>
          <v-button
            color="secondary"
            size="lg"
            has-min-width
            @click="closeModal"
          >
            Cancel
          </v-button>
        </v-button-group>
      </template>
    </v-modal>
    <v-modal
      v-if="showReviewModal"
      @close="closeReview"
    >
      <template #header>
        Document Review
      </template>
      <h4>
        Has this document been reviewed?
      </h4>
      <v-textarea
        :maxlength="255"
        :placeholder="placeholder"
        class="fieldset__input"
        @input="inputValue"
      />
      <template #footer>
        <v-button-group is-right>
          <v-button
            v-if="inputReviewInModal"
            size="lg"
            has-min-width
            @click="submitReview"
          >
            Finish
          </v-button>
          <v-button
            color="secondary"
            size="lg"
            has-min-width
            @click="closeReview"
          >
            Cancel
          </v-button>
        </v-button-group>
      </template>
    </v-modal>
  </PageTemplate>
</template>
<script>
  import PageTemplate from "../templates/Page";
  import Fieldset from "@/molecules/Fieldset";
  import ButtonGroup from "@/molecules/ButtonGroup";
  import Tabs from "@/molecules/Tabs";
  import Tab from "@/atoms/Tab";
  import Modal from "@/atoms/Modal";
  import Badge from "@/atoms/Badge";
  import DefinitionList from "@/atoms/DefinitionList";
  import Button from "@/atoms/Button";
  import Card from "@/molecules/Card";
  import Table from "@/molecules/Table";
  import Dropzone from "@/molecules/Dropzone";
  import Textarea from "@/atoms/Textarea";
  import StepIndicator from "@/molecules/StepIndicator";
  import FileUpload from '@/molecules/FileUpload'
  import Spinner from "@/atoms/Spinner";
  import constants from "../common/constants";
  import {
    GET_JOB,
    GET_JOB_LA,
    GET_JOB_DOCUMENTS,
    UPLOAD_JOB_DOCUMENT,
    DOWNLOAD_JOB_DOCUMENT,
    DOWNLOAD_JOB_DOCUMENTS,
    PREVIEW_JOB_DOCUMENT,
    REVIEW_JOB_DOCUMENT,
    GET_USER
  } from "@/store/actions.type";
  import flatPicker from "flatpickr";
  import "flatpickr/dist/flatpickr.css";
  import "flatpickr/dist/themes/airbnb.css";
  import {
    columnFilter,
    columnSort,
    buildBreadCrumb,
    buildDefinitionList,
    dateSort_ddmmyyyy
  } from "@/common/tableFunctions"
  import {formatFileSize, formatDate} from "@/common/formatting";
  import routes from "@/common/routes";


  export default {
    name: "Job",
    components: {
      "v-table": Table,
      "v-fieldset": Fieldset,
      "v-tabs": Tabs,
      "v-tab": Tab,
      "v-badge": Badge,
      "v-button": Button,
      "v-card": Card,
      "v-modal": Modal,
      "v-button-group": ButtonGroup,
      "v-dropzone": Dropzone,
      "v-definition-list": DefinitionList,
      "v-step-indicator": StepIndicator,
      "v-textarea": Textarea,
      "v-spinner": Spinner,
      'v-fileupload': FileUpload,
      PageTemplate,
    },
    filters: {
      formatFileSize: formatFileSize
    },
    data() {
      return {
        placeholder: "Please add your comments here*",
        components: {
          flatPicker
        },
        showModal: false,
        defaultStatues: [
          {
            "text": "Initiation"
          },
          {
            "text": "Preparation",
          },
          {
            "text": "Assessment",
          },
          {
            "text": "Submission",
          },
          {
            "text": "Decision"
          }
        ],
        statuses: [],
        received: {
          columns: [
            {
              label: 'data',
              field: 'data',
              hidden: 'true',
            },
            {
              label: 'Document Name',
              field: 'docName',
              sortFn: columnSort,
              thClass: 'sortable',
              filterOptions: {
                enabled: true,
                placeholder: 'Enter Document Name',
                filterFn: columnFilter
              },
            },
            {
              label: 'Review Comment',
              field: 'reviewed_comment',
              sortable: false
            },
            {
              label: 'Description',
              field: 'desc',
              sortable: false
            },
            {
              label: 'Date Uploaded',
              field: 'dateUploaded',
              sortFn: dateSort_ddmmyyyy,
              thClass: 'sortable shrink-cell',
              filterOptions: {
                enabled: true,
                placeholder: 'Date',
                filterFn: this.dateRangeFilter
              },
            },
            {
              label: 'Actions',
              field: 'actions',
              sortable: false,
              actions: 'view_and_download',
              thClass: 'shrink-cell',
              tdClass: 'no-wrap',
            },
            {
              label: 'Reviewed',
              field: 'reviewed',
              sortable: false,
              actions: 'checkbox_or_value',
              thClass: 'shrink-cell',
              tdClass: 'no-wrap',
            }
          ],
          rows: [],
          totalRecords: 0
        },
        submitted: {
          columns: [
            {
              label: 'data',
              field: 'data',
              hidden: 'true'
            },
            {
              label: 'Document Name',
              field: 'docName',
              sortFn: columnSort,
              thClass: 'sortable',
              filterOptions: {
                enabled: true,
                placeholder: 'Enter Document Name',
                filterFn: columnFilter
              },
            },
            {
              label: 'Description',
              field: 'desc',
              sortable: false
            },
            {
              label: 'Date Uploaded',
              field: 'dateUploaded',
              sortFn: dateSort_ddmmyyyy,
              thClass: 'sortable shrink-cell',
              filterOptions: {
                enabled: true,
                placeholder: 'Date',
                filterFn: columnFilter
              },
            },
            {
              label: 'Uploaded By',
              field: 'uploadedBy',
              sortable: false,
              thClass: 'shrink-cell',
              filterOptions: {
                enabled: true,
                placeholder: 'Name',
                filterFn: columnFilter
              },
            },
            {
              label: 'Actions',
              field: 'actions',
              sortable: false,
              actions: 'view_and_download',
              thClass: 'shrink-cell',
              tdClass: 'no-wrap',
            },
          ],
          rows: [],
          totalRecords: 0
        },
        breadcrumbs: [],
        jobNo: null,
        jobStatus: null,
        jobStatusBadgeText: null,
        jobStatusBadgeValue: null,
        jobDetails: [],
        siteDetails: [],
        LeadAssessor: [],
        uploadDescription: "",
        file: null,
        showReviewModal: false,
        inputReviewInModal: false,
        reviewId: '',
        reviewComment: '',
        isUploading: false,
        fileConfirmed: false
      };
    },
    computed: {
      documents() {
        return this.$store.state.jobDocuments.documents;
      },
      job(){
        return this.$store.state.job.job;
      },
      LA(){
          return this.$store.state.job.jobLA;
      }
    },
    mounted() {
      this.hideColumnIfNotAR(window.localStorage.getItem(constants.CURRENT_ROLE));
      this.getJob(this.$route.params.id);
      this.getDocuments(this.$route.params.id);
      let inputs = [
        'input[placeholder="Date"]',
      ];
      inputs.forEach(function (input) {
        flatPicker(input, {
          dateFormat: "d/m/Y",
          allowInput: true,
          theme: "light"
        });
      });
    },
    methods: {
      hideColumnIfNotAR: function(permissions){
          if(permissions === constants.NATA_ROLE_AR){
            this.$set( this.received.columns[6], 'hidden', true );
            this.$set( this.received.columns[2], 'hidden', true );
          }else if(permissions === constants.NATA_ROLE_TA){
            this.$set( this.received.columns[3], 'hidden', true );
          }
      },
      updateBreadCrumb: function(fn) {
        let breadcrumbs = [];
          breadcrumbs['Dashboard'] = routes.DASHBOARD;
          breadcrumbs['Jobs'] = routes.JOBS;
          breadcrumbs['Job ' + fn] = '';
          this.breadcrumbs = buildBreadCrumb(breadcrumbs);
        this.$store.dispatch('updateBreadCrumbAction', buildBreadCrumb(breadcrumbs));
      },
      downloadSelected(rows) {
        let selectedIds = rows.map( function (row) {
          return row.data.id;
        });
        this.$store.state.auth.isLoading = true;
        this.$store.dispatch(DOWNLOAD_JOB_DOCUMENTS, {selectedIds})
          .then(response => {
            return response;
          })
          .catch(error => {
            this.$notify({
              title: 'Error',
              text: error,
              type: 'error'
            });
          }).finally(() => {
            this.$store.state.auth.isLoading = false;
          });
      },
      downloadSelectedReceived() {
        let selectedRows = this.$refs.receivedDocumentTable.$refs.table.selectedRows;
          if(selectedRows.length > 0){
              this.downloadSelected(selectedRows);
          }
      },
      downloadSelectedSubmitted() {
        let selectedRows = this.$refs.submittedDocumentTable.$refs.table.selectedRows;
          if(selectedRows.length > 0){
              this.downloadSelected(selectedRows);
          }
      },
      dateRangeFilter(data, string) {
        return data.value.toString().indexOf(string) !== -1;
      },
      openModal() {
        this.showModal = true;
      },
      closeModal() {
        this.file = null;
        this.showModal = false;
        this.fileConfirmed = false;
        this.uploadDescription = '';
      },
      sendingEvent(file) {
        file.formData.append('jobId', this.jobId);
        file.formData.append('description', this.uploadDescription);
      },
      downloadDoc(params) {
        this.$store.state.auth.isLoading = true;
        let url = params;
        this.$store.dispatch(DOWNLOAD_JOB_DOCUMENT, {url})
          .then(response => {
            return response;
          })
          .catch(error => {
            this.$notify({
              title: 'Error',
              text: error,
              type: 'error'
            });
          }).finally(() => {
            this.$store.state.auth.isLoading = false;
          });
      },
      fileAdded(file) {
        this.file = file;
      },
      fileRemoved() {
        this.fileConfirmed = false;
        this.file = null;
      },
      getDocuments: function (jobid) {
        this.$store.dispatch(GET_JOB_DOCUMENTS, {jobid}).then(response => {
          this.received.rows = this.documents.received;
          this.submitted.rows = this.documents.submitted;
        }).catch(err => {
          // In case if user doesn't have access to this Job.
          // Clear out Job details - as well.
          setTimeout(() => {
            this.jobNo = null;
            this.jobStatus = null;
            this.jobStatusBadgeText = null;
            this.jobStatusBadgeValue = null;
            this.jobDetails = [];
            this.siteDetails = [];
            this.LeadAssessor = [];
          }, 100)
          this.$notify({
              title: 'Error',
              text: err.message,
              type: 'error',
              duration: 5000
            });
        });
      },
      getJob: function (job_id) {
        this.$store.dispatch(GET_JOB, {job_id}).then(response => {
          let res = this.job;
          this.jobStatus = res[0].stage;
          this.jobStatusBadgeText = res[0].status;
          this.jobStatusBadgeValue = 'In Progress' == res[0].status? 'accent': 'primary';
          this.jobNo = res[0].job_number;
          this.jobId = res[0].id;

          // update statuses with isActive
          this.defaultStatues.forEach((status) => {
            let s = {};
            s.text = status.text;
            if (status.text == this.jobStatus) {
              s.isActive = true
            }
            this.statuses.push(s)
          });

          let jobDetails = [];
          let siteDetails = [];
          this.updateBreadCrumb(this.jobNo);

          jobDetails['Job Type'] = res[0].type;
          jobDetails['Visit Date'] = formatDate(res[0].visit_date, constants.DD_MM_YYYY);
          this.jobDetails = buildDefinitionList(jobDetails);

          siteDetails['Site Name'] = res[0].site.name;
          siteDetails['Site ID'] = res[0].site.site_number;
          siteDetails['Phone'] = res[0].site.phone;
          siteDetails['Email'] = res[0].site.email;
          siteDetails['Address'] = res[0].site.address;
          siteDetails['Suburb'] = res[0].site.suburb;
          if (res[0].site.post_code != undefined) {
            siteDetails['Post Code'] = res[0].site.post_code;
          }
          siteDetails['State'] = res[0].site.country !== null ? (res[0].site.state + ', ' + res[0].site.country) : (res[0].site.state);
          this.siteDetails = buildDefinitionList(siteDetails);
          this.setJobLA(JSON.parse(res[0].lead_assessor));
        });
      },
      setJobLA: function(res) {
            let LeadAssessor = [];
            if (res !== undefined && res !== null) {
                LeadAssessor['Name'] = res.first_name + ' ' + res.last_name;
                LeadAssessor['Phone'] = res.phone;
                LeadAssessor['Email'] = res.email;
            } else {
                LeadAssessor['Name'] = '';
                LeadAssessor['Phone'] = '';
                LeadAssessor['Email'] = '';
            }
            this.LeadAssessor = buildDefinitionList(LeadAssessor);
      },
      inputChange(val) {
        this.uploadDescription = val;
      },
      previewDoc(params) {
        // If Microsoft Edge below 50 version
        if(window.navigator && window.navigator.msSaveOrOpenBlob) {
            this.downloadDoc(params);
            return;
        }
        let url = params;
        this.$store.dispatch(PREVIEW_JOB_DOCUMENT, {url})
          .then(response => {
                window.open(response, '_blank');
          })
          .catch(error => {
            this.$notify({
              title: 'Error',
              text: error,
              type: 'error'
            });
          })
      },
      uploadDocument: function () {
        this.isUploading = true;
        let file = this.file;
        let jobId = this.jobId;
        let description = this.uploadDescription;
        this.$store.dispatch(UPLOAD_JOB_DOCUMENT, {file, jobId, description})
          .then(response => {
            this.getDocuments(this.jobId);
            this.isUploading = false;
            this.closeModal();
            this.uploadDescription = '';
            this.$notify({
              title: 'Success',
              text: 'File successfully uploaded!',
              type: 'success'
            });
          })
          .catch(error => {
            this.isUploading = false;
            this.description = '';
            this.$notify({
              title: 'Error',
              text: error,
              type: 'error'
            });
          });
      },
      uploadError: function (errors) {
        for (var i = errors.length - 1; i >= 0; i--) {
          this.$notify({
            title: 'Error',
            text: errors[i],
            type: 'error'
          });
        }
      },
      showReview: function (params) {
        this.reviewId = params.id;
        this.showReviewModal = true;
      },
      closeReview: function () {
        //  uncheck selected box, remove all previous value if close modal
        document.getElementById(this.reviewId).checked = false;
        this.showReviewModal = false;
        this.inputReviewInModal = false;
        this.reviewComment = '';
        this.reviewId = '';
      },
      submitReview: function () {
        let reviewId = this.reviewId
        let reviewComment = this.reviewComment;
        this.$store.dispatch(REVIEW_JOB_DOCUMENT, {reviewComment, reviewId})
          .then(response => {
            this.closeReview();
            this.getDocuments(this.$route.params.id);
          })
          .catch(error => {
            //review is also an update function
            this.$notify({
              title: 'Error',
              text: error.error,
              type: 'error'
            });
          })
      },
      inputValue: function (data) {
        this.inputReviewInModal = true;
        this.reviewComment = data;
      }
    }
  }
</script>
